import type { EmailBrand } from '@duke-hq/email-templates/brand';
import type {
  OrganisationIdentity,
  OrganisationIdentityFormValues,
} from '@duke-hq/svc-api/admin/trpc/routers/organisation/schemas';

/**
 * The fields provisioning always writes, so an email never renders without them.
 * Derived from the identity rather than listed, so a field that stops being
 * nullable moves between the two helpers below without anyone remembering to.
 */
type AlwaysSet = {
  [K in keyof OrganisationIdentity]: OrganisationIdentity[K] extends string ? K : never;
}[keyof OrganisationIdentity];

type Nullable = Exclude<keyof OrganisationIdentity, AlwaysSet>;

/** What the form holds for one field: undefined until touched, null once cleared. */
function edited(
  draft: OrganisationIdentityFormValues,
  key: keyof OrganisationIdentity,
): string | null | undefined {
  const value = draft[key];
  if (value === undefined) {
    return undefined;
  }
  return value === '' ? null : value;
}

function nullable(
  identity: OrganisationIdentity,
  draft: OrganisationIdentityFormValues,
  key: Nullable,
): string | null {
  const value = edited(draft, key);
  return value === undefined ? identity[key] : value;
}

/** Cleared falls back to what is stored, since an email cannot render without it. */
function alwaysSet(
  identity: OrganisationIdentity,
  draft: OrganisationIdentityFormValues,
  key: AlwaysSet,
): string {
  return edited(draft, key) ?? identity[key];
}

/** What is saved, with the form's current values over it. Emptied previews as absent. */
export function toEmailBrand(
  identity: OrganisationIdentity,
  draft: OrganisationIdentityFormValues,
): EmailBrand {
  return {
    organisationName: alwaysSet(identity, draft, 'organisationName'),
    legalName: nullable(identity, draft, 'legalName'),
    rtoCode: nullable(identity, draft, 'rtoCode'),
    supportEmail: nullable(identity, draft, 'supportEmail'),
    supportPhoneVanity: nullable(identity, draft, 'supportPhoneVanity'),
    supportPhoneDigits: nullable(identity, draft, 'supportPhoneDigits'),
    contactUrl: nullable(identity, draft, 'contactUrl'),
    privacyNoticeUrl: nullable(identity, draft, 'privacyNoticeUrl'),
    logoUrl: nullable(identity, draft, 'logoUrl'),
    logoGreyscaleUrl: nullable(identity, draft, 'logoGreyscaleUrl'),
    linkColour: alwaysSet(identity, draft, 'linkColour'),
    buttonBackgroundColour: alwaysSet(identity, draft, 'buttonBackgroundColour'),
    buttonTextColour: alwaysSet(identity, draft, 'buttonTextColour'),
  };
}
